import React, { useState, useLayoutEffect, useMemo, useReducer } from "react";
import axios from "axios";

// Custom data
import { ALLOWED_TYPES, MAX_ATTACHMENT_SIZE, ERRORS } from "../../data/Attachments";

// Custom Components
import ApplicationSubmitted from "./ApplicationSubmitted";
import AttachmentContainer from "../General/AttachmentContainer";
import FamilyMemberContainer from "./FamilyMemberContainer";

// Animation Imports
import { Fade } from "react-reveal";

// Form Imports

import Select from 'react-select';
import { InputMask } from 'primereact/inputmask';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from "primereact/inputtextarea";

// Select Options

import { GenderOptions } from "./DropdownOptions/GenderOptions";
import { RelationshipStatusOptions } from "./DropdownOptions/RelationshipStatusOptions";
import { StateOptions } from "./DropdownOptions/StateOptions";
import { BleedingDisorderRelationshipOptions } from "./DropdownOptions/BleedingDisorderRelationshipOptions";
import { BleedingDisorderTypeOptions } from "./DropdownOptions/BleedingDisorderTypeOptions";
import { BleedingDisorderSeverityOptions } from "./DropdownOptions/BleedingDisorderSeverityOptions";
import { MethodOptions } from "./DropdownOptions/MethodOptions";
import { TypeOptions } from "./DropdownOptions/TypeOptions";

// Data handling
import { FAMILY_MEMBER_TEMPLATE, INITIAL_STATE } from "./ApplicationStateTemplate";
import { applicationReducer } from "./ApplicationReducer";
import ApplicationHelper from "../../data/ApplicationHelper";
import ErrorMessage from "../General/ErrorMessage";
import SectionStatusHeader from "./SectionStatusHeader";

const maximumBirthdayLength = 10;
const maximumFamilyMembers = 11;

// Attachment Errors

const maxAttachments = 10;



function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
};

export default function ApplicationForm(props) {
    // Width / Height
    const [width, height] = useWindowSize();

    // Attachments

    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState(false);
    const [attachmentErrorMessage, setAttachmentErrorMessage] = useState("");

    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            fontWeight: state.isSelected ? "bold" : "normal",
            fontSize: width <= 1260 ? "1.2rem" : "1.4rem",
            color: "#2d3436",
            backgroundColor: "#fff",
            "&:hover": {
                backgroundColor: "#f0f0f0",
            },
        }),
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? "#2d3436" : "#fff",
            margin: "12px 0 12px",
            height: width <= 1260 ? "48px" : "55px",
        }),
    };

    // Reducer

    const [state, dispatch] = useReducer(applicationReducer, {
        ...INITIAL_STATE,
        // Update initial state based on user log in 
        firstName: props.userData ? props.userData.firstName : INITIAL_STATE.firstName,
        lastName: props.userData ? props.userData.lastName : INITIAL_STATE.lastName,
        email: props.userData ? props.userData.email : INITIAL_STATE.email,
    });

    // Application front end states

    const generalComplete = useMemo(() => {
        if (state.gender === null && state.relationshipStatus === null) {
            return false;
        };

        return ApplicationHelper.verifyFirstName(state.firstName) &&
            ApplicationHelper.verifyLastName(state.lastName) &&
            ApplicationHelper.verifyEmail(state.email) &&
            ApplicationHelper.verifyBirthday(state.birthday) &&
            ApplicationHelper.verifyPhone(state.phone) &&
            ApplicationHelper.verifyIncome(state.income) > 0 &&
            state.gender !== null &&
            state.relationshipStatus !== null;

    }, [state.firstName, state.lastName, state.email, state.birthday,
    state.phone, state.income,
    state.gender ? state.gender.value : null,
    state.relationshipStatus ? state.relationshipStatus.value : null]);

    const familyComplete = useMemo(() => {
        if (state.familyInformation.length === 0) {
            if (state.isMinor === false && state.livesAlone === true) {
                return true;
            } else {
                return false;
            }
        }
        if (ApplicationHelper.verifyFamilyInformation(state.familyInformation)) {
            return true;
        } else {
            return false;
        }
    }, [state.isMinor, state.livesAlone, state.familyInformation]);


    const addressComplete = useMemo(() => {
        if (state.state === null) return false;

        return ApplicationHelper.verifyStreetAddress(state.streetAddress) &&
            ApplicationHelper.verifyCity(state.city) &&
            state.state.value.length > 0 && ApplicationHelper.verifyZip(state.zip);
    }, [state.streetAddress, state.city, state.state, state.zip]);

    const medicalComplete = useMemo(() => {
        if (state.bleedingDisorder === false) {
            return true;
        }

        if (state.bleedingDisorderRelationship !== null && state.bleedingDisorderType !== null
            && ApplicationHelper.verifyHTC(state.HTC)) {
            return true;
        } else {
            return false;
        }
    }, [state.bleedingDisorder, state.bleedingDisorderRelationship, state.BleedingDisorderType,
    state.HTC
    ]);

    const infoComplete = useMemo(() => {
        if (state.type !== null && state.method !== null) {
            if (ApplicationHelper.verifyAmount(state.amount) &&
                ApplicationHelper.verifyDetails(state.details) && state.otherApplication !== null) {
                if (state.otherApplication === true) {
                    if (ApplicationHelper.verifyOtherLocations(state.otherLocations)) {
                        return true;
                    } else {
                        return false;
                    }
                }
                return true;
            }
        }
        return false;

    }, [state.type, state.method, state.amount, state.details, state.otherApplication, state.otherLocations]);

    const referralComplete = useMemo(() => {
        if (state.referred !== null) {
            if (state.referred) {
                return ApplicationHelper.verifyReferrer(state.referrer) && state.permissions;
            }
            return true;
        }
    }, [state.referred, state.referrer, state.permissions]);

    const applicationComplete = useMemo(() => {
        return generalComplete && addressComplete && medicalComplete && infoComplete && referralComplete;
    }, [generalComplete, addressComplete, medicalComplete, infoComplete, referralComplete]);

    // Family Functions

    const addFamilyMember = () => {
        if (state.familyInformation.length < maximumFamilyMembers) {
            dispatch({ type: 'ADD_FAMILY_MEMBER' });
        }
    }

    // Takes the field which is being updated and the input (generally from event.target.value) 
    // Handles according to pre-existing state logic in a centralized function
    const validateInput = (field, input) => {
        var finalValue = input;
        // Defaults to true 
        var shouldDispatch = true;

        switch (field) {
            case 'firstName':
            case 'lastName':
                if (!ApplicationHelper.filterNumbersAndSymbols(input)) {
                    shouldDispatch = false;
                }
                break;
            case 'birthday':
                dispatch({ type: 'UPDATE_FIELD', field: 'birthday', value: finalValue });
                dispatch({ type: 'UPDATE_FIELD', field: 'isMinor', value: ApplicationHelper.verifyIsMinor(finalValue) });
                return;

            case 'zip':
            case 'amount':
                if (!ApplicationHelper.verifyIsNumber(input)) {
                    shouldDispatch = false;
                }
                break;

            case 'details':
                if (input.length > 1500) {
                    shouldDispatch = false;
                }
                break;

            default:
                break;


        }

        if (input.length > 2000) {
            shouldDispatch = false;
        }

        if (shouldDispatch) {
            dispatch({ type: 'UPDATE_FIELD', field: field, value: finalValue });
        }
    }

    // Filter special characters

    // Action Handling

    const handleSubmit = (event) => {
        event.preventDefault();
        if (ApplicationHelper.verifyIsNumber(state.income) && ApplicationHelper.verifyIsNumber(state.amount)) {
            const formData = new FormData();
            formData.append('firstName', state.firstName);
            formData.append('lastName', state.lastName);
            formData.append('email', state.email);
            formData.append('birthday', state.birthday);
            formData.append('phone', state.phone);
            formData.append('income', state.income);
            formData.append('gender', state.gender.value);
            formData.append('relationshipStatus', state.relationshipStatus.value);
            formData.append('streetAddress', state.streetAddress);
            formData.append('streetAddress2', state.streetAddress2);
            formData.append('city', state.city);
            formData.append('state', state.state.value);
            formData.append('ZIP', state.zip);
            formData.append('bleedingDisorder', state.bleedingDisorder);
            formData.append('bleedingDisorderRelationship', state.bleedingDisorderRelationship.value);
            formData.append('bleedingDisorderType', state.bleedingDisorderType.value);
            formData.append('bleedingDisorderSeverity', state.bleedingDisorderSeverity.value);
            formData.append('HTC', state.HTC);
            formData.append('method', state.method.value);
            formData.append('type', state.type.value);
            formData.append('amount', state.amount);
            formData.append('details', state.details);
            formData.append('otherApplication', state.otherApplication);
            formData.append('otherLocations', state.otherLocations);
            formData.append('referred', state.referred);
            formData.append('referrer', state.referrer);
            formData.append('newsletterOptIn', state.newsletterOptIn);
            for (const attachment of attachments) {
                formData.append('attachments', attachment);
            }
            formData.append('isMinor', ApplicationHelper.verifyIsMinor(state.birthday));
            formData.append('livesAlone', state.livesAlone);
            formData.append('familyData', JSON.stringify(state.familyInformation));
            axios.post('/api/applications', formData, { withCredentials: true, })
                .then((res) => {
                    if (res.data.status) {
                        dispatch({ type: 'UPDATE_FIELD', field: 'submitted', value: true });
                        dispatch({ type: 'UPDATE_FIELD', field: 'token', value: res.data.token });
                    } else {
                        dispatch({ type: 'SET_ERROR', error: true, errorMessage: res.data.message });
                    }
                })
        } else {
            dispatch({ type: 'SET_ERROR', error: true, errorMessage: 'Both Income and Amount Requested Must Be Numbers!' });
        }
    }

    // Attachments

    const onFileUpload = (event) => {
        const currentFile = event.target.files[0];
        // Check File Size
        if (currentFile.size <= MAX_ATTACHMENT_SIZE) {
            // Check File Type
            if (ALLOWED_TYPES.includes(currentFile.type)) {
                setAttachmentError(false);
                setAttachmentErrorMessage('');
                setAttachments(attachments.concat(event.target.files[0]));
                event.target.value = null;
            } else {
                setAttachmentError(true);
                setAttachmentErrorMessage(ERRORS.typeError);
                event.target.value = null;
            }
        } else {
            setAttachmentError(true);
            setAttachmentErrorMessage(ERRORS.sizeError);
            event.target.value = null;
        }
    }

    const onFileDelete = (file) => {
        const newAttachments = attachments.filter(attachment => attachment !== file);
        setAttachments(newAttachments);
        if (attachmentErrorMessage === ERRORS.maxError) {
            setAttachmentError(false);
            setAttachmentErrorMessage('');
        }
    }

    // General Info Section

    const renderGeneralSection = () => {
        if (width <= 480) {
            return (
                <>
                    <div className="application-section">
                        <h1 className="header">General Information</h1>
                        <SectionStatusHeader complete={generalComplete} />

                        <div className="section-items">
                            <div className="section-item">
                                <label for="firstName">
                                    First Name: {!ApplicationHelper.verifyFirstName(state.firstName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='firstName' value={state.firstName} onChange={(event) => validateInput('firstName', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="lastName">
                                    Last Name: {!ApplicationHelper.verifyLastName(state.lastName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='lastName' value={state.lastName} onChange={(event) => validateInput('lastName', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="email">
                                    Email: {ApplicationHelper.verifyEmail(state.email) ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                    <InputText id='email' value={state.email} onChange={(event) => validateInput('email', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="birthday">
                                    Birthday: {ApplicationHelper.verifyBirthday(state.birthday) ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                    <InputMask mask="99/99/9999" slotChar="mm/dd/yyyy" value={state.birthday} onChange={(event) => validateInput('birthday', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label>
                                    Phone: {!ApplicationHelper.verifyPhone(state.phone) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputMask mask="(999) 999-9999" slotChar="(___) ___-____" value={state.phone} onChange={(event) => validateInput('phone', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="income">
                                    Monthly Income (Estimate): {!ApplicationHelper.verifyIncome(state.income) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='income' value={state.income} onChange={(event) => validateInput('income', event.target.value)} />
                                </label>
                            </div>

                        </div>

                        <div className="section-item">
                            <label>
                                Gender: {state.gender ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.gender}
                                    onChange={(option) => validateInput('gender', option)} options={GenderOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Relationship Status: {state.relationshipStatus ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.relationshipStatus}
                                    onChange={(option) => validateInput('relationshipStatus', option)} options={RelationshipStatusOptions} />
                            </label>
                        </div>

                    </div>
                </>
            )
        }

        return (
            <>
                <div className="application-section">
                    <h1 className="header">General Information</h1>
                    <SectionStatusHeader complete={generalComplete} />

                    <div className="section-items">
                        <div className="section-item">
                            <label for="firstName">
                                First Name: {!ApplicationHelper.verifyFirstName(state.firstName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='firstName' value={state.firstName} onChange={(event) => validateInput('firstName', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="lastName">
                                Last Name: {!ApplicationHelper.verifyLastName(state.lastName) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='lastName' value={state.lastName} onChange={(event) => validateInput('lastName', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="email">
                                Email: {ApplicationHelper.verifyEmail(state.email) ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <InputText id='email' value={state.email} onChange={(event) => validateInput('email', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="birthday">
                                Birthday: {ApplicationHelper.verifyBirthday(state.birthday) ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <InputMask mask="99/99/9999" slotChar="mm/dd/yyyy" value={state.birthday} onChange={(event) => validateInput('birthday', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Phone: {!ApplicationHelper.verifyPhone(state.phone) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputMask mask="(999) 999-9999" slotChar="(___) ___-____" value={state.phone} onChange={(event) => validateInput('phone', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="income">
                                Monthly Income (Estimate): {!ApplicationHelper.verifyIncome(state.income) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='income' value={state.income} onChange={(event) => validateInput('income', event.target.value)} />
                            </label>
                        </div>

                    </div>

                    <div className="two-item-section">
                        <div className="section-item">
                            <label>
                                Gender: {state.gender ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.gender}
                                    onChange={(option) => validateInput('gender', option)} options={GenderOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Relationship Status: {state.relationshipStatus ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.relationshipStatus}
                                    onChange={(option) => validateInput('relationshipStatus', option)} options={RelationshipStatusOptions} />
                            </label>
                        </div>
                    </div>

                </div>
            </>
        )
    }

    // Family Section

    const renderMinorInformation = () => {
        if (state.isMinor) {
            return (
                <>
                    <p>As a minor, you are required to put your parent / caretakers information before proceeding.</p>
                </>
            )
        }
    }

    const renderFamilyMemberInformation = () => {
        return (
            <>
                <ul className="family-member-information">
                    {state.familyInformation.map((familyMember, index) => {
                        return (
                            <li key={index}>
                                <FamilyMemberContainer
                                    familyMember={familyMember}
                                    dispatch={dispatch}
                                    ApplicationHelper={ApplicationHelper}
                                    index={index}
                                    selectStyles={selectStyles}
                                />
                            </li>
                        )
                    })
                    }
                </ul>
            </>
        )
    }

    const renderFamilyMemberInformationAddSection = () => {
        if (state.livesAlone === false || state.isMinor === true) {
            return (
                <>
                    <div className="family-member-add-information-section">
                        <p>Please list all inviduals residing with you and any financial co-dependents.</p>
                        {renderMinorInformation()}
                        <p>(Limit 10)</p>
                        {renderFamilyMemberInformation()}
                        <button className={state.familyInformation.length < maximumFamilyMembers ?
                            'approved-button' : 'denied-button'} onClick={addFamilyMember}>Add Family Member</button>
                    </div>
                </>
            )
        }
    }

    const renderMinorNotification = () => {
        if (state.isMinor) return <h2>Applicants under 18 are required to include at least one family member.</h2>;
    }

    const renderFamilySection = () => {
        return (
            <>
                <div className="family-section application-section">
                    <h1 className="header">Family Information</h1>
                    <SectionStatusHeader complete={familyComplete} />
                    <h2>Do You Or the Person You're Applying For Live With Anyone or Have Any Financial Co-Dependents?</h2>
                    {renderMinorNotification()}
                    <div className="apply-buttons-container">
                        <button className={state.livesAlone === true ?
                            "apply-button declined no" : "apply-button no"}
                            onClick={() => {
                                dispatch({ type: 'UPDATE_FIELD', field: 'livesAlone', value: true });
                            }}>No <i class="fa-solid fa-xmark"></i></button>

                        <button className={state.livesAlone === false ?
                            "apply-button approved yes" : "apply-button yes"}
                            onClick={() => {
                                dispatch({ type: 'UPDATE_FIELD', field: 'livesAlone', value: false });
                            }}>Yes <i class="fa-solid fa-check"></i></button>
                    </div>

                    {renderFamilyMemberInformationAddSection()}
                </div>
            </>
        )
    }

    // Address Info Section

    const renderAddressSection = () => {
        if (width <= 850) {
            return (
                <>
                    <div className="application-section">
                        <h1 className="header">Address Information</h1>
                        <SectionStatusHeader complete={addressComplete} />
                        <div className="section-items">

                            <div className="section-item">
                                <label for="streetAddress">
                                    Street Address: {!ApplicationHelper.verifyStreetAddress(state.streetAddress) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='streetAddress' value={state.streetAddress} onChange={(event) => validateInput('streetAddress', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="streetAddress2">
                                    Street Address 2:
                                    <InputText id='streetAddress2' value={state.streetAddress2} onChange={(event) => validateInput('streetAddress2', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="city">
                                    City: {!ApplicationHelper.verifyCity(state.city) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='city' value={state.city} onChange={(event) => validateInput('city', event.target.value)} />
                                </label>
                            </div>

                            <div className="section-item">
                                <label for="zip">
                                    ZIP: {!ApplicationHelper.verifyZip(state.zip) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                    <InputText id='zip' value={state.zip} onChange={(event) => validateInput('zip', event.target.value)} />
                                </label>
                            </div>
                        </div>

                        <div className="section-item">
                            <label>
                                State: {state.state ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.state}
                                    onChange={(option) => validateInput('state', option)} options={StateOptions} />
                            </label>
                        </div>
                    </div>
                </>
            )
        }

        return (
            <>
                <div className="application-section">
                    <h1 className="header">Address Information</h1>
                    <SectionStatusHeader complete={addressComplete} />

                    <div className="section-items">

                        <div className="section-item">
                            <label for="streetAddress">
                                Street Address: {!ApplicationHelper.verifyStreetAddress(state.streetAddress) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='streetAddress' value={state.streetAddress} onChange={(event) => validateInput('streetAddress', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="streetAddress2">
                                Street Address 2:
                                <InputText id='streetAddress2' value={state.streetAddress2} onChange={(event) => validateInput('streetAddress2', event.target.value)} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="city">
                                City: {!ApplicationHelper.verifyCity(state.city) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='city' value={state.city} onChange={(event) => validateInput('city', event.target.value)} />
                            </label>
                        </div>
                    </div>
                    <div className="two-item-section">
                        <div className="section-item">
                            <label>
                                State: {state.state ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.state}
                                    onChange={(option) => validateInput('state', option)} options={StateOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="zip">
                                ZIP: {!ApplicationHelper.verifyZip(state.zip) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id='zip' value={state.zip} onChange={(event) => validateInput('zip', event.target.value)} />
                            </label>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    // Medical Info Section

    const renderMedicalSectionDetails = () => {
        if (state.bleedingDisorder) {
            if (width <= 736) {
                return (
                    <>
                        <div className="section-item">
                            <label>
                                Relationship to Bleeding Disorder: {state.bleedingDisorderRelationship.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderRelationship}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderRelationship', value: option })} 
                                    options={BleedingDisorderRelationshipOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Type: {state.bleedingDisorderType.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderType}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderType', value: option })} 
                                    options={BleedingDisorderTypeOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Severity: {state.bleedingDisorderSeverity.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderSeverity}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderSeverity', value: option })} 
                                    options={BleedingDisorderSeverityOptions} />
                            </label>
                        </div>

                        <label for="htc">
                            HTC: {!ApplicationHelper.verifyHTC(state.HTC) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                            <InputText id='htc' value={state.HTC} onChange={(event) => validateInput('HTC', event.target.value)} />
                        </label>
                    </>
                )
            }
            return (
                <>
                    <div className="section-items">
                        <div className="section-item">
                            <label>
                                Relationship to Bleeding Disorder: {state.bleedingDisorderRelationship.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderRelationship}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderRelationship', value: option })} 
                                    options={BleedingDisorderRelationshipOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Type: {state.bleedingDisorderType.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderType}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderType', value: option })} 
                                    options={BleedingDisorderTypeOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Severity: {state.bleedingDisorderSeverity.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.bleedingDisorderSeverity}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: 'bleedingDisorderSeverity', value: option })} 
                                    options={BleedingDisorderSeverityOptions} />
                            </label>
                        </div>


                    </div>

                    <label for="htc">
                        HTC: {!ApplicationHelper.verifyHTC(state.HTC) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                        <InputText id='htc' value={state.HTC} onChange={(event) => validateInput('HTC', event.target.value)} />
                    </label>
                </>

            )
        }
    }

    const renderMedicalSection = () => {
        return (
            <>
                <div className="application-section">
                    <h1 className="header">Medical Information</h1>
                    <SectionStatusHeader complete={medicalComplete} />

                    <h2 className="header bleeding-disorder-question">Are You or the Person You're Applying for Affected by a Bleeding Disorder?</h2>
                    <div className="apply-buttons-container">
                        <button className={state.bleedingDisorder === false ? "apply-button declined no" : "apply-button no"}
                            onClick={() => {
                                validateInput('bleedingDisorder', false);
                            }}>No <i class="fa-solid fa-xmark"></i></button>

                        <button className={state.bleedingDisorder === true ? "apply-button approved yes" : "apply-button yes"}
                            onClick={() =>
                                validateInput('bleedingDisorder', true)}>Yes <i class="fa-solid fa-check"></i></button>
                    </div>

                    {renderMedicalSectionDetails()}
                </div>
            </>
        )
    }

    // Application Info Section


    const renderOtherApplicationPrompt = () => {
        if (state.otherApplication) {
            return (
                <div className="section-item">
                    <label>
                        Where Else Have You Applied? (Please list all): {!ApplicationHelper.verifyOtherLocations(state.otherLocations) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                        <InputText id='otherLocations' value={state.otherLocations} onChange={(event) => validateInput('otherLocations', event.target.value)} />
                    </label>
                </div>

            )
        }
    }

    const renderApplicationInfoSection = () => {
        if (width <= 736) {
            return (
                <>
                    <div className="application-section">
                        <h1 className="header">Request Information</h1>
                        <p>Please remember to limit your request to 1 bill per application.</p>
                        <p>Round the amount of money you're requesting up to the nearest whole number.</p>
                        <SectionStatusHeader complete={infoComplete} />

                        <div className="section-item">
                            <label>
                                Type of Assistance: {state.type ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.type}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: "type", value: option })} options={TypeOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Method of Assistance: {state.method ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.method}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: "method", value: option })} options={MethodOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="assistanceAmount">
                                Amount Requested (USD, Rounded): {!ApplicationHelper.verifyAmount(state.amount) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id="assistanceAmount" value={state.amount} onChange={(event) => { validateInput('amount', event.target.value) }} />
                            </label>
                        </div>

                        <h2 className="header application-reason-header">Reason for Applying</h2>
                        <p>Explain in as much detail as necessary why you're applying for assistance,
                            include anything within reason that you feel will influence our decision (limit 1500 characters).</p>
                        <div>
                            <label for="assistanceDetails">
                                ({1500 - state.details.length} Characters Remaining):
                            </label>
                        </div>

                        <div>
                            <InputTextarea autoResize={true} rows={5} cols={30}
                                id="details" value={state.details} onChange={(event) => { validateInput('details', event.target.value) }} />
                        </div>

                        <h2>Are You Applying for Assistance From Other Sources?</h2>

                        <div className="apply-buttons-container">
                            <button className={state.otherApplication === false ? "apply-button declined no" : "apply-button no"}
                                onClick={() => {
                                    dispatch({ type: 'UPDATE_FIELD', field: 'otherApplication', value: false });
                                }}>No <i class="fa-solid fa-xmark"></i></button>

                            <button className={state.otherApplication === true ? "apply-button approved yes" : "apply-button yes"}
                                onClick={() => { dispatch({ type: 'UPDATE_FIELD', field: 'otherApplication', value: true }); }}>
                                Yes <i class="fa-solid fa-check"></i></button>
                        </div>

                        {renderOtherApplicationPrompt()}
                    </div>
                </>
            )
        }
        return (
            <>
                <div className="application-section">
                    <h1 className="header">Request Information</h1>
                    <p>Please remember to limit your request to 1 bill per application.</p>
                    <p>Round the amount of money you're requesting up to the nearest whole number.</p>
                    <SectionStatusHeader complete={infoComplete} />

                    <div className="section-items">
                        <div className="section-item">
                            <label>
                                Type of Assistance: {state.type ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.type}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: "type", value: option })} options={TypeOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label>
                                Method of Assistance: {state.method ? <i class="fa-solid fa-check green-check"></i> : <i class="fa-thin fa-asterisk red-asterisk" />}
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition={'fixed'}
                                    value={state.method}
                                    onChange={(option) => dispatch({ type: 'UPDATE_FIELD', field: "method", value: option })} options={MethodOptions} />
                            </label>
                        </div>

                        <div className="section-item">
                            <label for="assistanceAmount">
                                Amount Requested (USD, Rounded): {!ApplicationHelper.verifyAmount(state.amount) ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                                <InputText id="assistanceAmount" value={state.amount} onChange={(event) => { validateInput('amount', event.target.value) }} />
                            </label>
                        </div>
                    </div>

                    <h2 className="header application-reason-header">Reason for Applying</h2>
                    <p>Explain in as much detail as necessary why you're applying for assistance,
                        include anything within reason that you feel will influence our decision (limit 1500 characters).</p>
                    <div>
                        <label for="assistanceDetails">
                            ({1500 - state.details.length} Characters Remaining):
                        </label>
                    </div>

                    <div>
                        <InputTextarea autoResize={true} rows={5} cols={30}
                            id="details" value={state.details} onChange={(event) => { validateInput('details', event.target.value) }} />
                    </div>

                    <h2>Are You Applying for Assistance From Other Sources?</h2>

                    <div className="apply-buttons-container">
                        <button className={state.otherApplication === false ? "apply-button declined no" : "apply-button no"}
                            onClick={() => {
                                dispatch({ type: 'UPDATE_FIELD', field: 'otherApplication', value: false });
                            }}>No <i class="fa-solid fa-xmark"></i></button>

                        <button className={state.otherApplication === true ? "apply-button approved yes" : "apply-button yes"}
                            onClick={() => { dispatch({ type: 'UPDATE_FIELD', field: 'otherApplication', value: true }); }}>
                            Yes <i class="fa-solid fa-check"></i></button>
                    </div>

                    {renderOtherApplicationPrompt()}
                </div>
            </>
        )
    }

    // Attachments Section

    const renderAttachments = () => {
        if (attachments.length > 0) {
            return (
                <>
                    <ul className="attachments-list">
                        {attachments.map((attachment, index) => {
                            return (
                                <li key={index}>
                                    <AttachmentContainer attachment={attachment}
                                        onFileDelete={onFileDelete}
                                    />
                                </li>
                            )
                        })}
                    </ul>
                </>
            )
        } else {
            return (
                <>
                    <h3>Nothing Yet!</h3>
                </>
            )
        }
    }

    const renderAttachmentSection = () => {
        return (
            <>
                <div className="application-section">
                    <h1 className="header">Add Attachments</h1>
                    <p>Limit your request to 1 bill per application.</p>
                    <p>Applications with multiple bills attached will be denied.</p>
                    <p>Please ensure a fast response by attaching all relevant documents, including information for payment.</p>
                    <p>Applications which lack sufficient payment information will be automatically denied after 15 days.</p>
                    <p>To attach a file, click 'Upload Attachment' and select the desired file.</p>
                    <p>You can repeat this process to attach multiple files (Max 10 Uploads per Application).</p>
                    <p>If you accidentally upload a file, you can click 'Remove Attachment' to remove it.</p>
                    <h2 className="header uploaded-attachments-header">Currently Uploaded Attachments</h2>
                    {renderAttachments()}
                    <p>Accepted file types (Max Size: 8MB): </p>
                    <p>.png, .jpeg, .jpg, .webp, .pdf, .rtf, .doc, .docx, .txt, .csv, .xls, .xlsx, .ppt, .pptx</p>
                    <h2 className="error">{attachmentError ? attachmentErrorMessage : ''}</h2>
                    <div className="attachment-button-row">
                        <label onChange={onFileUpload} htmlFor="formId">
                            <input name="" type="file" id="formId" hidden />
                            <div className="attachment-button approved-button">
                                Upload File
                            </div>
                        </label>
                    </div>
                </div>
            </>
        )
    }

    // Referral Section

    const renderPermissionError = () => {
        if (state.permissionError) {
            return (
                <>
                    <Fade bottom>
                        <h2 className="error">We Cannot Accept Referral Applications Without Consent to Speak to the Referrer</h2>
                    </Fade>
                </>
            )
        }
    }

    const renderReferralSectionDetails = () => {
        if (state.referred === true) {
            return (
                <>
                    <div className="section-item">
                        <label for="referrer">
                            Who Referred You?: {state.referrer.length === 0 ? <i class="fa-thin fa-asterisk red-asterisk" /> : <i class="fa-solid fa-check green-check"></i>}
                            <InputText id='referrer' value={state.referrer} onChange={(event) => validateInput('referrer', event.target.value)} />
                        </label>
                    </div>
                    <div className="section-item">
                        <h2 className="header consent-question">Do You Give Consent for This Person to Discuss Your Information with HUGS on Your Behalf?</h2>
                        <div className="apply-buttons-container">
                            <button className={state.permissions === false && state.permissions !== null ?
                                "apply-button declined no" : "apply-button no"}
                                onClick={() => {
                                    dispatch({ type: 'UPDATE_FIELD', field: 'permissions', value: false });
                                    dispatch({ type: 'UPDATE_FIELD', field: 'permissionError', value: true });
                                }}>No <i class="fa-solid fa-xmark"></i></button>

                            <button className={state.permissions === true ? "apply-button approved yes" : "apply-button yes"}
                                onClick={() => {
                                    dispatch({ type: 'UPDATE_FIELD', field: 'permissions', value: true });
                                    dispatch({ type: 'UPDATE_FIELD', field: 'permissionError', value: false });
                                }}>Yes <i class="fa-solid fa-check"></i></button>
                        </div>
                    </div>

                    {renderPermissionError()}
                </>
            )
        }
    }

    const renderReferralSection = () => {
        return (
            <>
                <div className="application-section">
                    <h1 className="header">Referral Information</h1>
                    <SectionStatusHeader complete={referralComplete} />
                    <h2 className="header referred-header">Were You Referred to Us?</h2>
                    <div className="apply-buttons-container">
                        <button className={state.referred === false ? "apply-button declined no" : "apply-button no"}
                            onClick={() => {
                                dispatch({ type: 'UPDATE_FIELD', field: 'referred', value: false });
                            }}>No <i class="fa-solid fa-xmark"></i></button>

                        <button className={state.referred === true ? "apply-button approved yes" : "apply-button yes"}
                            onClick={() => {
                                dispatch({ type: 'UPDATE_FIELD', field: 'referred', value: true });
                            }}>Yes <i class="fa-solid fa-check"></i></button>
                    </div>
                    {renderReferralSectionDetails()}
                </div>
            </>
        )
    }

    const renderAttachmentReview = () => {
        if (attachments.length === 0) {
            return (
                <>
                    <h3>None</h3>
                    <p>We recommend adding any relevant attachments in order to speed up your assistance process!</p>
                </>
            )
        }

        return (
            <>
                <ul className="attachments-list">
                    {attachments.map((attachment, index) => {
                        return (
                            <li key={index}>
                                <h2>{attachment.name}</h2>
                            </li>
                        )
                    })}
                </ul>
            </>

        )
    }

    // Review Section

    const renderReviewSection = () => {
        return (
            <>
                <div className="application-section-review">
                    <h1 className="header">Review Your Application</h1>

                    <div className="review-container">
                        <div className="review-section">
                            <h2 className="header">General</h2>
                            <SectionStatusHeader complete={generalComplete} />
                            <div className="review-items">
                                <div className="review-item">
                                    <h3>First Name: </h3> <p>{state.firstName ? state.firstName : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Last Name: </h3> <p>{state.lastName ? state.lastName : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Email: </h3> <p>{state.email ? state.email : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Birthday: </h3> <p>{state.birthday ? state.birthday : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Phone: </h3> <p>{state.phone ? state.phone : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Income: </h3> <p>{state.income ? "$" + state.income + "/mo" : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Gender: </h3> <p>{state.gender ? state.gender.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Relationship Status: </h3> <p>{state.relationshipStatus ? state.relationshipStatus.value : "N/A"}</p>
                                </div>

                            </div>


                        </div>

                        <div className="review-section">
                            <h2 className="header">Address Information</h2>
                            <SectionStatusHeader complete={addressComplete} />

                            <div className="review-items">
                                <div className="review-item">
                                    <h3>Street Address: </h3> <p>{state.streetAddress ? state.streetAddress : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Street Address 2: </h3> <p>{state.streetAddress2 ? state.streetAddress2 : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>City: </h3> <p>{state.city ? state.city : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>State: </h3> <p>{state.state ? state.state.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>ZIP: </h3> <p>{state.zip ? state.zip : "N/A"}</p>
                                </div>
                            </div>

                        </div>

                        <div className="review-section">
                            <h2 className="header">Medical Information</h2>
                            <SectionStatusHeader complete={medicalComplete} />

                            <div className="review-items">
                                <div className="review-item">
                                    <h3>Bleeding Disorder: </h3> <p>{state.bleedingDisorder ? "Yes" : "No"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Relationship to Bleeding Disorder: </h3> <p>{state.bleedingDisorder ? state.bleedingDisorderRelationship.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Type: </h3> <p>{state.bleedingDisorder ? state.bleedingDisorderType.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Severity: </h3> <p>{state.bleedingDisorder ? state.bleedingDisorderSeverity.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>HTC: </h3> <p>{state.bleedingDisorder ? state.HTC : "N/A"}</p>
                                </div>
                            </div>
                        </div>

                        <div className="review-section">
                            <h2 className="header">Request Information</h2>
                            <SectionStatusHeader complete={infoComplete} />

                            <div className="review-items">
                                <div className="review-item">
                                    <h3>Type of Assistance: </h3> <p>{state.type ? state.type.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Method of Assistance: </h3> <p>{state.method ? state.method.value : "N/A"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Amount Requested (USD, Rounded): </h3> <p>{state.amount ? state.amount : "N/A"}</p>
                                </div>
                            </div>

                            <h3>Reason for Applying:</h3>
                            <p>{state.details}</p>
                        </div>

                        <div className="review-section">
                            <h2 className="header">Attachments</h2>
                            {renderAttachmentReview()}

                        </div>

                        <div className="review-section">
                            <h2 className="header">Referral Information</h2>
                            <SectionStatusHeader complete={referralComplete} />

                            <div className="review-items">
                                <div className="review-item">
                                    <h3>Referred: </h3> <p>{state.referred ? "Yes" : "No"}</p>
                                </div>

                                <div className="review-item">
                                    <h3>Who Referred You?: </h3> <p>{state.referred ? state.referrer : "N/A"}</p>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </>
        )
    }

    // Submit Section

    const renderNewsletterOptIn = () => {
        return (
            <div className="application-form-newsletter-opt-in">
                <h3 className="header">Sign up for our newsletter? </h3>
                <button className={state.newsletterOptIn ?
                    "application-form-newsletter-opt-in-button approved-button-selected"
                    : "application-form-newsletter-opt-in-button denied-button-selected"} onClick={() => {
                        dispatch({ type: 'UPDATE_FIELD', field: 'newsletterOptIn', value: !state.newsletterOptIn })
                    }}>{state.newsletterOptIn ? <i class="fa-solid fa-check"></i> : <i class="fa-solid fa-xmark"></i>}</button>
            </div>
        )
    }

    const renderApplicationCompleted = () => {

        if (applicationComplete) {
            return (
                <div className="application-section submit-section">
                    <h1 className="header">Submit Your Application</h1>
                    <h2 className="completed section-status header">Application Completed!</h2>
                    <div className="submit-section-text">
                        <p>By submitting this form, I certify that the information I have provided in the above is true and correct.</p>
                        <p>
                            I consent to the release of pertinent information contained in this application to Hearts Unite to Give Support Inc.,
                            other social service agencies which distribute emergency financial assistance,
                            the company or individual to receive funds as necessary to complete the services to my household, or to provide statistics on emergency assistance,
                            or as a guard against duplicate assistance.
                            I also consent to release of patient information to the federal government and those utility companies which require documentation of recipient's funds. </p>
                    </div>
                    {renderNewsletterOptIn()}
                    <ErrorMessage error={state.error} errorMessage={state.errorMessage} />
                    <button className="apply-submit-button approved-button" onClick={handleSubmit}>Submit Application</button>
                </div>
            )
        }


        return (
            <div className="application-section submit-section">

                <h1 className="header">Submit Your Application</h1>
                <h2 className="incomplete section-status header">Application Incomplete</h2>
                <div className="submit-section-text">
                    <p>By submitting this form, I certify that the information I have provided in the above is true and correct.</p>
                    <p>
                        I consent to the release of pertinent information contained in this application to Hearts Unite to Give Support Inc.,
                        other social service agencies which distribute emergency financial assistance,
                        the company or individual to receive funds as necessary to complete the services to my household, or to provide statistics on emergency assistance,
                        or as a guard against duplicate assistance.
                        I also consent to release of patient information to the federal government and those utility companies which require documentation of recipient's funds. </p>
                </div>
                {renderNewsletterOptIn()}
                <ErrorMessage error={state.error} errorMessage={state.errorMessage} />
                <button className="apply-submit-button denied-button" onClick={() => {
                    dispatch({ type: 'SET_ERROR', error: true, errorMessage: "Please Complete Application Before Submitting!" });
                }}>Unable to Submit</button>
            </div>
        )
    }

    const renderDisclaimerSection = () => {
        return (
            <div className="application-section">
                <h1 className="header">Disclaimer: </h1>
                <div className="disclaimer-list">
                    <p>1. Our processing time can be up to 21 days and submitting incomplete information may elongate the processing time. Please be careful to ensure that all information is present when submitting to help ensure a fast response.</p>
                    <p>2. We are unable to log into user accounts to assist with payments. Please provide us direct contact or invoice information in your application. DO NOT send passwords or login information to us in applications.</p>
                    <p>3. The individual requesting assistance should be the guardian of the household, under their own name.</p>
                    <p>4. All applications are decided on a case to case basis.</p>
                </div>
            </div>
        )
    }

    const renderSubmitSection = () => {
        return (
            <>
                {renderApplicationCompleted()}
            </>
        )
    }

    if (state.submitted) {
        return (
            <>
                <ApplicationSubmitted guest={props.guest} name={state.firstName} token={state.token} email={state.email}
                />
            </>
        )
    }

    return (
        <>

            <div className="application-form">
                <Fade>
                    {renderGeneralSection()}
                </Fade>

                <Fade>
                    {renderFamilySection()}
                </Fade>

                <Fade>
                    {renderAddressSection()}
                </Fade>

                <Fade>
                    {renderMedicalSection()}
                </Fade>

                <Fade>
                    {renderApplicationInfoSection()}
                </Fade>

                <Fade>
                    {renderAttachmentSection()}
                </Fade>

                <Fade>
                    {renderReferralSection()}
                </Fade>

                <Fade>
                    {renderReviewSection()}
                </Fade>

                <Fade>
                    {renderDisclaimerSection()}
                </Fade>

                <Fade>
                    {renderSubmitSection()}
                </Fade>
            </div>
        </>
    )
}